import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { analytics } from "@/lib/analytics";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = "form" | "sent";

const COUNTRIES = [
  ["US", "United States"],
  ["CA", "Canada"],
  ["GB", "United Kingdom"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["IE", "Ireland"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["CH", "Switzerland"],
  ["AT", "Austria"],
  ["SE", "Sweden"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["FI", "Finland"],
  ["IT", "Italy"],
  ["ES", "Spain"],
  ["PT", "Portugal"],
  ["PL", "Poland"],
  ["CZ", "Czech Republic"],
  ["JP", "Japan"],
  ["KR", "South Korea"],
  ["CN", "China"],
  ["HK", "Hong Kong"],
  ["SG", "Singapore"],
  ["IN", "India"],
  ["BR", "Brazil"],
  ["MX", "Mexico"],
  ["AR", "Argentina"],
  ["ZA", "South Africa"],
  ["IL", "Israel"],
  ["RU", "Russia"],
  ["UA", "Ukraine"],
] as const;

const emptyForm = { name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", country: "US" };

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 520 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 519px)");
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

export default function RegisterModal({ open, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>("form");
  const [form, setForm] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  if (!open) return null;

  const set = (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.registerBuyer({
        name:    form.name.trim(),
        email:   form.email.trim(),
        phone:   form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        city:    form.city.trim() || undefined,
        state:   form.state.trim() || undefined,
        zip:     form.zip.trim() || undefined,
        country: form.country || "US",
      });
      analytics.trackSignUp("Email");
      setStep("sent");
      onSuccess?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("form");
    setForm({ ...emptyForm });
    setError(null);
    onClose();
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 12px",
    background: "#141414", border: "1px solid #2a2a2a", borderRadius: 8,
    color: "#e0e0e0", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
    outline: "none",
  };
  const lbl: React.CSSProperties = { display: "block", fontSize: 13, color: "#aaa", marginBottom: 6 };
  const col2: React.CSSProperties = isMobile
    ? { display: "flex", flexDirection: "column", gap: 14 }
    : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        background: "#111", border: "1px solid #222", borderRadius: 14,
        padding: isMobile ? 20 : 28, width: "100%", maxWidth: 480,
        position: "relative", maxHeight: "90vh", overflowY: "auto",
      }}>
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 14, right: 14,
            background: "none", border: "none", color: "#555",
            fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4,
          }}
        >
          ×
        </button>

        {step === "form" ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 4 }}>Register as a Buyer</h2>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                Create a free account to contact sellers. We'll send a verification link to your email.
              </p>
            </div>

            {error && (
              <div style={{ background: "#1e0a0a", border: "1px solid #4a1a1a", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#ff6b6b", fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Name + Email */}
              <div style={col2}>
                <div>
                  <label htmlFor="reg-name" style={lbl}>Full Name <span style={{ color: "#e8c070" }}>*</span></label>
                  <input id="reg-name" type="text" required value={form.name} onChange={set("name")} placeholder="Jane Smith" style={inp} />
                </div>
                <div>
                  <label htmlFor="reg-email" style={lbl}>Email Address <span style={{ color: "#e8c070" }}>*</span></label>
                  <input id="reg-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@example.com" style={inp} />
                </div>
              </div>

              {/* Phone + Country */}
              <div style={col2}>
                <div>
                  <label htmlFor="reg-phone" style={lbl}>Phone <span style={{ color: "#555", fontSize: 11 }}>(optional)</span></label>
                  <input id="reg-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" style={inp} />
                </div>
                <div>
                  <label htmlFor="reg-country" style={lbl}>Country</label>
                  <select id="reg-country" value={form.country} onChange={set("country")} style={{ ...inp, cursor: "pointer" }}>
                    {COUNTRIES.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Street Address */}
              <div>
                <label htmlFor="reg-street" style={lbl}>Street Address <span style={{ color: "#555", fontSize: 11 }}>(for shipping)</span></label>
                <input id="reg-street" type="text" value={form.address} onChange={set("address")} placeholder="123 Main Street" style={inp} />
              </div>

              {/* City — full width on all screens */}
              <div>
                <label htmlFor="reg-city" style={lbl}>City</label>
                <input id="reg-city" type="text" value={form.city} onChange={set("city")} placeholder="Springfield" style={inp} />
              </div>

              {/* State + ZIP — side by side on all screens (short fields) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label htmlFor="reg-state" style={lbl}>State / Province</label>
                  <input id="reg-state" type="text" value={form.state} onChange={set("state")} placeholder="IL" maxLength={6} style={inp} />
                </div>
                <div>
                  <label htmlFor="reg-zip" style={lbl}>ZIP / Postal Code</label>
                  <input id="reg-zip" type="text" value={form.zip} onChange={set("zip")} placeholder="62701" maxLength={10} style={inp} />
                </div>
              </div>

              <button
                type="submit" disabled={loading}
                style={{
                  width: "100%", padding: "11px 20px",
                  background: loading ? "#555" : "#e8c070", color: "#0a0a0a",
                  border: "none", borderRadius: 8, fontWeight: 700,
                  fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: "inherit", marginTop: 4,
                }}
              >
                {loading ? "Sending…" : "Create Account & Verify Email"}
              </button>
            </form>

            <p style={{ color: "#444", fontSize: 11, marginTop: 14, textAlign: "center" }}>
              By registering you agree to our{" "}
              <a href="/terms" style={{ color: "#666", textDecoration: "underline" }}>Terms & Conditions</a>
            </p>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Check your email!</h2>
            <p style={{ color: "#888", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              We sent a verification link to <strong style={{ color: "#e8c070" }}>{form.email}</strong>.
              Click the link to verify your account, then you'll be able to contact sellers.
            </p>
            <button
              onClick={handleClose}
              style={{
                padding: "10px 28px", background: "#1a1a1a",
                border: "1px solid #333", borderRadius: 8, color: "#ccc",
                fontSize: 14, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
